import React, { useState, useEffect, useCallback } from 'react';
import { Users, Search, Edit, Send, RefreshCw, CheckCircle, Clock, X } from 'lucide-react';
import axios from 'axios';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const IS_PROD = !window.location.hostname.includes('localhost');

const participantsApi = {
  list: (eventId) =>
    IS_PROD
      ? api.get(`/simple.php?action=participants&event_id=${eventId}`)
      : axios.get(`/api/admin/events/${eventId}/sessions`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        }),
  update: (sessionId, data) =>
    IS_PROD
      ? api.post(`/simple.php?action=participants&session_id=${sessionId}&_method=PUT`, data)
      : axios.put(`/api/admin/sessions/${sessionId}`, data, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        }),
  resend: (sessionId) =>
    IS_PROD
      ? null // resend not available in production (needs local Node.js)
      : axios.post(`/api/admin/sessions/${sessionId}/resend`, {}, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        }),
};

const ParticipantsManagement = () => {
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [participants, setParticipants] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [editForm, setEditForm] = useState({ first_name: '', last_name: '', email: '' });
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState(null);

  useEffect(() => {
    api.get('/simple.php?action=events').then(res => {
      if (res.data.success) setEvents(res.data.data);
    }).catch(() => toast.error('Failed to load events'));
  }, []);

  const fetchParticipants = useCallback(async (eventId) => {
    if (!eventId) return;
    setLoading(true);
    setSearch('');
    try {
      const res = await participantsApi.list(eventId);
      if (res.data.success) {
        setParticipants(res.data.data);
        setFiltered(res.data.data);
      }
    } catch {
      toast.error('Failed to load participants');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedEventId) fetchParticipants(selectedEventId);
    else { setParticipants([]); setFiltered([]); }
  }, [selectedEventId, fetchParticipants]);

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(participants.filter(p =>
      `${p.first_name} ${p.last_name} ${p.email} ${p.division_name || ''}`.toLowerCase().includes(q)
    ));
  }, [search, participants]);

  const openEdit = (p) => {
    setEditingSession(p);
    setEditForm({ first_name: p.first_name, last_name: p.last_name, email: p.email });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await participantsApi.update(editingSession.session_id, editForm);
      toast.success('Participant updated. Email status reset — will resend on next batch.');
      setEditingSession(null);
      fetchParticipants(selectedEventId);
    } catch {
      toast.error('Failed to update participant');
    } finally {
      setSaving(false);
    }
  };

  const handleResend = async (p) => {
    if (IS_PROD) {
      toast('Resend only works locally. Run the batch script from your PC.', { icon: 'ℹ️' });
      return;
    }
    if (!window.confirm(`Resend certificate to ${p.first_name} ${p.last_name} <${p.email}>?`)) return;
    setResending(p.session_id);
    try {
      await participantsApi.resend(p.session_id);
      toast.success(`Certificate queued for ${p.first_name} ${p.last_name}`);
      fetchParticipants(selectedEventId);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to resend');
    } finally {
      setResending(null);
    }
  };

  const sentCount = participants.filter(p => p.email_sent == 1).length;
  const unsentCount = participants.length - sentCount;

  return (
    <div className="container" style={{ padding: '24px 16px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
        <Users size={28} color="#3b82f6" />
        <div>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '700', color: '#1e293b' }}>Participants</h1>
          <p style={{ margin: 0, color: '#64748b', fontSize: '0.9rem' }}>
            View, edit, and resend certificates per event
          </p>
        </div>
      </div>

      {/* Event Selector */}
      <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: '20px' }}>
        <label style={{ fontWeight: '600', color: '#374151', display: 'block', marginBottom: '8px' }}>Select Event</label>
        <select
          value={selectedEventId}
          onChange={e => setSelectedEventId(e.target.value)}
          style={{ width: '100%', maxWidth: '480px', padding: '10px 12px', borderRadius: '8px', border: '1.5px solid #e2e8f0', fontSize: '0.95rem', color: '#1e293b' }}
        >
          <option value="">— Choose an event —</option>
          {events.map(ev => <option key={ev.events_id} value={ev.events_id}>{ev.event_name}</option>)}
        </select>
      </div>

      {/* Participants Table */}
      {selectedEventId && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          {/* Toolbar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '600', color: '#1e293b' }}>
                {participants.length} participant{participants.length !== 1 ? 's' : ''}
              </h2>
              {participants.length > 0 && (
                <>
                  <span style={{ background: '#dcfce7', color: '#166534', borderRadius: '20px', padding: '2px 10px', fontSize: '0.78rem', fontWeight: '600' }}>
                    ✓ {sentCount} sent
                  </span>
                  <span style={{ background: '#fef9c3', color: '#854d0e', borderRadius: '20px', padding: '2px 10px', fontSize: '0.78rem', fontWeight: '600' }}>
                    ⏳ {unsentCount} pending
                  </span>
                </>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <div style={{ position: 'relative' }}>
                <Search size={15} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search name, email..."
                  style={{ paddingLeft: '32px', padding: '8px 12px 8px 32px', borderRadius: '8px', border: '1.5px solid #e2e8f0', fontSize: '0.875rem', width: '220px' }}
                />
              </div>
              <button onClick={() => fetchParticipants(selectedEventId)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: '6px' }}>
                <RefreshCw size={16} />
              </button>
            </div>
          </div>

          {loading ? (
            <p style={{ textAlign: 'center', color: '#64748b', padding: '40px' }}>Loading...</p>
          ) : filtered.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#94a3b8', padding: '40px' }}>No participants found.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                    {['#', 'Name', 'Email', 'Division', 'Submitted', 'Cert', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '700', color: '#475569', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p, i) => (
                    <tr key={p.session_id} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                      <td style={{ padding: '10px 12px', color: '#94a3b8' }}>{i + 1}</td>
                      <td style={{ padding: '10px 12px', fontWeight: '600', color: '#1e293b', whiteSpace: 'nowrap' }}>
                        {p.first_name} {p.last_name}
                      </td>
                      <td style={{ padding: '10px 12px', color: '#475569' }}>{p.email}</td>
                      <td style={{ padding: '10px 12px', color: '#64748b' }}>{p.division_name || '—'}</td>
                      <td style={{ padding: '10px 12px', color: '#64748b', whiteSpace: 'nowrap' }}>
                        {p.submitted_at ? new Date(p.submitted_at).toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        {p.email_sent == 1
                          ? <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#059669', fontWeight: '600', fontSize: '0.8rem' }}><CheckCircle size={14} /> Sent</span>
                          : <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#d97706', fontWeight: '600', fontSize: '0.8rem' }}><Clock size={14} /> Pending</span>
                        }
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button
                            onClick={() => openEdit(p)}
                            title="Edit name/email"
                            style={{ background: '#eff6ff', border: 'none', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', color: '#3b82f6', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', fontWeight: '600' }}
                          >
                            <Edit size={13} /> Edit
                          </button>
                          <button
                            onClick={() => handleResend(p)}
                            disabled={resending === p.session_id}
                            title={IS_PROD ? 'Run batch script locally to resend' : 'Resend certificate email'}
                            style={{ background: IS_PROD ? '#f1f5f9' : '#f0fdf4', border: 'none', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', color: IS_PROD ? '#94a3b8' : '#059669', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', fontWeight: '600' }}
                          >
                            <Send size={13} /> {resending === p.session_id ? '...' : 'Resend'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Edit Modal */}
      {editingSession && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' }}>
          <div style={{ background: 'white', borderRadius: '16px', width: '100%', maxWidth: '460px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#1e293b' }}>Edit Participant</h2>
              <button onClick={() => setEditingSession(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}><X size={20} /></button>
            </div>
            <form onSubmit={handleSave} style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={labelStyle}>First Name</label>
                  <input
                    value={editForm.first_name}
                    onChange={e => setEditForm(f => ({ ...f, first_name: e.target.value }))}
                    required style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Last Name</label>
                  <input
                    value={editForm.last_name}
                    onChange={e => setEditForm(f => ({ ...f, last_name: e.target.value }))}
                    required style={inputStyle}
                  />
                </div>
              </div>
              <div>
                <label style={labelStyle}>Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))}
                  required style={inputStyle}
                />
              </div>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#f59e0b', background: '#fffbeb', padding: '8px 12px', borderRadius: '6px' }}>
                Saving will reset the certificate status to "Pending" so the corrected cert can be resent.
              </p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setEditingSession(null)}
                  style={{ padding: '10px 20px', borderRadius: '8px', border: '1.5px solid #e2e8f0', background: 'white', cursor: 'pointer', fontWeight: '600', color: '#374151' }}>
                  Cancel
                </button>
                <button type="submit" disabled={saving}
                  style={{ padding: '10px 24px', borderRadius: '8px', background: '#3b82f6', color: 'white', border: 'none', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: '600', opacity: saving ? 0.7 : 1 }}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const labelStyle = { display: 'block', fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '0.875rem' };
const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1.5px solid #e2e8f0', fontSize: '0.9rem', color: '#1e293b', boxSizing: 'border-box' };

export default ParticipantsManagement;
